import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  createPayLink,
  listPayLinks,
  cancelPayLink,
  type CreatePayLinkRequest,
  type ListPayLinksParams,
} from "../../services/payLinkService";
import {
  getBusinessProfiles,
  listBusinesses,
  setActiveBusiness,
  type BusinessProfilesResponse,
  type SetActiveBusinessResponse,
} from "../../services/businessProfileService";
import {
  businessProfilesQueryKey,
  enrichBusinessProfiles,
  normalizeBusinessList,
  normalizeBusinessProfilesPayload,
  syncActiveBusinessContext,
} from "./businessProfiles";
import { getUser } from "../../services/userService";
import { assertBusinessAccess } from "../../utils/businessAccess";

export type BusinessTwoFactorAction =
  | "crypto_send"
  | "fiat_withdraw"
  | "88pay_payment"
  | "internal_transfer"
  | "donation"
  | "zar8_yield_position"
  | "zar8_yield_lock"
  | "investment_portfolio_deposit"
  | "investment_portfolio_withdraw"
  | (string & {});

export interface BusinessTwoFactorAuth {
  enabled?: boolean;
  requiredForActions?: BusinessTwoFactorAction[];
}

export interface BusinessPreferences {
  localCurrency?: string;
  performancePeriod?: string;
  language?: string;
  showBalances?: boolean;
  hideZeroBalances?: boolean;
  [key: string]: unknown;
}

export interface BusinessMeProfile {
  _id?: string;
  businessId?: string;
  twoFactorAuth?: BusinessTwoFactorAuth | boolean;
  security?: {
    twoFactorAuth?: { enabled?: boolean };
    pinSet?: boolean;
    twoFaLogin?: boolean;
  };
  preferences?: BusinessPreferences;
  settings?: {
    preferences?: BusinessPreferences;
    [key: string]: unknown;
  };
  businessProfile?: {
    twoFactorAuth?: BusinessTwoFactorAuth | boolean;
    businessId?: string;
    preferences?: BusinessPreferences;
    [key: string]: unknown;
  };
  [key: string]: unknown;
}

export const getBusinessPreferences = (
  businessMe: BusinessMeProfile | null | undefined
): BusinessPreferences => {
  if (!businessMe) return {};
  return (
    businessMe.preferences ||
    businessMe.settings?.preferences ||
    businessMe.businessProfile?.preferences ||
    {}
  );
};

export const getBusinessLocalCurrencyRaw = (
  businessMe: BusinessMeProfile | null | undefined
): string | undefined => {
  const prefs = getBusinessPreferences(businessMe);
  const fromPrefs =
    typeof prefs.localCurrency === "string" && prefs.localCurrency.trim()
      ? prefs.localCurrency.trim()
      : undefined;
  if (fromPrefs) return fromPrefs;
  return undefined;
};

export const isBusinessTwoFactorEnabled = (
  businessMe: BusinessMeProfile | null | undefined
): boolean => {
  if (!businessMe) return false;

  const readFlag = (
    value: BusinessTwoFactorAuth | boolean | undefined
  ): boolean | null => {
    if (typeof value === "boolean") return value;
    if (value && typeof value === "object" && "enabled" in value) {
      return value.enabled === true;
    }
    return null;
  };

  const fromRoot = readFlag(businessMe.twoFactorAuth);
  if (fromRoot !== null) return fromRoot;

  const fromBusinessProfile = readFlag(
    businessMe.businessProfile?.twoFactorAuth
  );
  if (fromBusinessProfile !== null) return fromBusinessProfile;

  return businessMe.security?.twoFactorAuth?.enabled ?? false;
};

export const getBusinessTwoFactorRequiredActions = (
  businessMe: BusinessMeProfile | null | undefined
): BusinessTwoFactorAction[] => {
  if (!businessMe) return [];

  const readActions = (
    value: BusinessTwoFactorAuth | boolean | undefined
  ): BusinessTwoFactorAction[] | null => {
    if (
      value &&
      typeof value === "object" &&
      Array.isArray(value.requiredForActions)
    ) {
      return value.requiredForActions;
    }
    return null;
  };

  return (
    readActions(businessMe.twoFactorAuth) ||
    readActions(businessMe.businessProfile?.twoFactorAuth) ||
    []
  );
};

export const businessMeQueryKey = (businessId: string | null | undefined) =>
  ["businessMe", businessId || "none"] as const;

export const useBusinessMe = (businessId: string | null | undefined) => {
  return useQuery({
    queryKey: businessMeQueryKey(businessId),
    queryFn: async () => {
      if (!businessId) {
        return null;
      }
      return (await getUser(businessId)) as unknown as BusinessMeProfile;
    },
    enabled: Boolean(businessId),
    staleTime: 1000 * 30,
  });
};

export const getBusinessMeLocalCurrency = (
  businessMe: BusinessMeProfile | null | undefined
): "ZAR" | "USD" | undefined => {
  const raw = getBusinessLocalCurrencyRaw(businessMe);
  if (!raw) return undefined;
  return raw.trim().toUpperCase() === "USD" ? "USD" : "ZAR";
};

export const useInvalidateBusinessMe = () => {
  const queryClient = useQueryClient();
  return async (businessId?: string | null) => {
    if (businessId && businessId.trim()) {
      await queryClient.invalidateQueries({
        queryKey: businessMeQueryKey(businessId.trim()),
      });
    }
    await queryClient.invalidateQueries({
      queryKey: ["businessMe"],
      exact: false,
    });
  };
};

export const useBusinessProfiles = () => {
  return useQuery({
    queryKey: businessProfilesQueryKey,
    queryFn: async () => {
      const [profilesResponse, businessListResponse] = await Promise.all([
        getBusinessProfiles().catch(() => null),
        listBusinesses().catch(() => null),
      ]);

      const profiles = normalizeBusinessProfilesPayload(profilesResponse);
      const businessList = normalizeBusinessList(businessListResponse);

      return enrichBusinessProfiles(profiles, businessList);
    },
    staleTime: 1000 * 60 * 5,
  });
};

export const useSetActiveBusiness = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (businessId: string | null) => {
      const cachedProfiles = queryClient.getQueryData<BusinessProfilesResponse>(
        businessProfilesQueryKey
      );
      if (cachedProfiles) {
        assertBusinessAccess(businessId, cachedProfiles.businesses);
      }
      return setActiveBusiness({ businessId });
    },
    onSuccess: async (payload: SetActiveBusinessResponse) => {
      await syncActiveBusinessContext(
        queryClient,
        payload.activeBusinessId ?? null
      );
    },
  });
};

export const usePayLinks = (
  businessId: string | null,
  params: ListPayLinksParams = {}
) => {
  return useQuery({
    queryKey: ["payLinks", businessId, params],
    queryFn: async () => {
      if (!businessId) {
        return {
          payLinks: [],
          pagination: {
            page: 1,
            limit: params.limit || 20,
            total: 0,
            totalPages: 0,
          },
        };
      }

      return listPayLinks(businessId, params);
    },
    enabled: Boolean(businessId),
    staleTime: 1000 * 30,
  });
};

export const useCreatePayLink = (businessId: string | null) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (payload: CreatePayLinkRequest) => {
      if (!businessId) throw new Error("Business ID is required");
      return createPayLink(businessId, payload);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["payLinks", businessId] });
    },
  });
};

export const useCancelPayLink = (businessId: string | null) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (payLinkId: string) => {
      if (!businessId) throw new Error("Business ID is required");
      return cancelPayLink(businessId, payLinkId);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["payLinks", businessId] });
    },
  });
};
